/**
 * Recipe unit helpers shared by the inventory UI.
 *
 * The backend registry is the source of truth and is loaded through
 * `GET /finance/stock-units`. Keeping the conversion rules here lets the
 * recipe editor show a live conversion and cost without a round trip for
 * every keystroke, while the option list itself cannot drift from the backend.
 */

import type { StockUnit, StockUnitKind } from "../types";

export type UnitKind = StockUnitKind | "custom";

export interface ResolvedUnit {
  key: string;
  /** Canonical code, or null for an unknown legacy/custom unit. */
  code: string | null;
  label: string;
  kind: UnitKind;
  factorToBase: number;
}

export interface UnitOption {
  value: string;
  label: string;
}

export interface UnitOptionGroup {
  label: string;
  options: UnitOption[];
}

function normaliseUnitText(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\.$/, "");
}

function findUnit(raw: unknown, units: StockUnit[]): StockUnit | undefined {
  const text = normaliseUnitText(raw);
  if (!text) return undefined;
  return units.find(
    (unit) =>
      unit.code === text ||
      unit.aliases.some((alias) => normaliseUnitText(alias) === text),
  );
}

/**
 * Resolve a stored unit or alias to its canonical definition. Unknown values
 * remain custom units keyed by their normalised text, so legacy data keeps
 * working without being offered as a new choice.
 */
export function resolveUnit(raw: unknown, units: StockUnit[]): ResolvedUnit | null {
  const text = normaliseUnitText(raw);
  if (!text) return null;

  const known = findUnit(text, units);
  if (known) {
    return {
      key: known.code,
      code: known.code,
      label: known.code,
      kind: known.kind,
      factorToBase: Number(known.factor_to_base),
    };
  }

  return {
    key: text,
    code: null,
    label: String(raw).trim(),
    kind: "custom",
    factorToBase: 1,
  };
}

export function resolveUnitCode(raw: unknown, units: StockUnit[]): string | null {
  return resolveUnit(raw, units)?.code ?? null;
}

/**
 * The units a recipe author may choose for an item. The item's own spelling
 * comes first and is always offered, even when it is a legacy custom unit.
 */
export function unitOptions(stockUnit: unknown, units: StockUnit[]): UnitOption[] {
  const raw = String(stockUnit ?? "").trim();
  if (!raw) return [];

  const known = findUnit(raw, units);
  if (!known) return [{ value: raw, label: raw }];

  const own: UnitOption = { value: raw, label: raw };
  if (known.kind === "count") return [own];

  const others = units
    .filter((unit) => unit.kind === known.kind && unit.code !== known.code)
    .map((unit) => ({ value: unit.code, label: unit.code }));
  return [own, ...others];
}

/** Grouped options for the stock-item unit dropdown. */
export function stockUnitGroups(units: StockUnit[]): UnitOptionGroup[] {
  const labels: Record<StockUnitKind, string> = {
    mass: "Weight",
    volume: "Volume",
    count: "Count",
  };
  return (["mass", "volume", "count"] as const)
    .map((kind) => ({
      label: labels[kind],
      options: units
        .filter((unit) => unit.kind === kind)
        .map((unit) => ({
          value: unit.code,
          label: `${unit.code} — ${unit.name}`,
        })),
    }))
    .filter((group) => group.options.length > 0);
}

export function convertQuantity(
  quantity: number,
  fromUnit: unknown,
  toUnit: unknown,
  units: StockUnit[],
): number | null {
  const amount = Number(quantity);
  if (!Number.isFinite(amount)) return null;

  const from = resolveUnit(fromUnit, units);
  const to = resolveUnit(toUnit, units);
  if (!from || !to) return null;

  if (from.kind !== to.kind) return null;
  if (from.kind === "count" || from.kind === "custom") {
    return from.key === to.key ? amount : null;
  }

  const converted = (amount * from.factorToBase) / to.factorToBase;
  return Number.isFinite(converted) ? converted : null;
}

export function roundQuantity(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 1000) / 1000;
}
